let _ = t => t,
  _t,
  _t2,
  _t3,
  _t4,
  _t5,
  _t6;
import { createContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import * as styledComponents from 'styled-components';
import { BREAKPOINTS } from 'foundations-components/transitional/components/Grid';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import debounce from 'react-utils/debounce';
import { produce } from 'immer';
import { css as cssWithTheme } from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const theme = {
  activeTheme: 'light',
  colors: {
    HIGH_CONTRAST_PRIMARY: '#1d0cff',
    HIGH_CONTRAST_LINK: '#0000c5',
    HIGH_CONTRAST_HOVER: '#cee2ff'
  },
  sizes: {
    GRID_BREAKPOINT_SMALL: `${BREAKPOINTS.sm}px`,
    GRID_BREAKPOINT_MEDIUM: `${BREAKPOINTS.md}px`,
    GRID_BREAKPOINT_LARGE: `${BREAKPOINTS.lg}px`,
    GRID_BREAKPOINT_EXTRA_LARGE: `${BREAKPOINTS.xl}px`,
    PUBLIC_NAV_FULL_HEIGHT: '80px',
    PUBLIC_NAV_REDUCED_HEIGHT: '60px',
    PUBLIC_PAGE_WIDTH: '1080px',
    PUBLIC_PAGE_WIDTH_LARGE: '1460px',
    ACADEMY_PAGE_WIDTH: '1376px',
    ACADEMY_PAGE_PADDING_X: '48px'
  },
  times: {
    SLIDEIN_MODAL_TRANSITION_TIMING: '300ms'
  }
};
const {
  default: styled,
  css,
  ThemeProvider: StyledComponentsThemeProvider,
  createGlobalStyle,
  keyframes,
  ThemeConsumer
} = styledComponents;
export const useThemeStore = create()(persist(set => ({
  theme: 'light',
  setTheme: newTheme => set({
    theme: newTheme
  })
}), {
  name: 'academy:theme'
}));
const ThemeProvider = ({
  children
}) => {
  const {
    theme: themeName
  } = useThemeStore();
  const dynamicTheme = useMemo(() => Object.assign({}, theme, {
    activeTheme: themeName
  }), [themeName]);
  return /*#__PURE__*/_jsx(StyledComponentsThemeProvider, {
    theme: dynamicTheme,
    children: children
  });
};
export { css, createGlobalStyle, keyframes, ThemeProvider, ThemeConsumer, cssWithTheme };
export default styled;

// Consumers must use the mediaQuery() helper and never define @container rules directly.
const CONTAINER_NAME = 'academy-root';
const NAVBAR_BREAKPOINT = 1060;
const containerDevices = {
  mobile: `(max-width: ${BREAKPOINTS.sm}px)`,
  tablet: `(max-width: ${BREAKPOINTS.md}px)`,
  laptop: `(max-width: ${BREAKPOINTS.lg}px)`,
  desktop: `(min-width: ${BREAKPOINTS.xl}px)`,
  notMobile: `(min-width: ${BREAKPOINTS.sm}px)`,
  notTablet: `(min-width: ${BREAKPOINTS.md}px)`,
  notLaptop: `(min-width: ${BREAKPOINTS.lg}px)`,
  mobileNavbar: `(max-width: ${NAVBAR_BREAKPOINT - 1}px)`,
  desktopNavbar: `(min-width: ${NAVBAR_BREAKPOINT}px)`
};
const windowDevices = {
  windowMobile: `(max-width: ${BREAKPOINTS.sm}px)`,
  windowTablet: `(max-width: ${BREAKPOINTS.md}px)`,
  windowLaptop: `(max-width: ${BREAKPOINTS.lg}px)`,
  windowDesktop: `(min-width: ${BREAKPOINTS.xl}px)`,
  windowNotMobile: `(min-width: ${BREAKPOINTS.sm}px)`,
  windowNotTablet: `(min-width: ${BREAKPOINTS.md}px)`,
  windowNotLaptop: `(min-width: ${BREAKPOINTS.lg}px)`,
  touchscreen: '(hover: none) and (pointer: coarse)'
};
const devices = Object.assign({}, containerDevices, windowDevices);
/**
 * Warning: this function only emits a `@media` query and does not emit a
 * `@container` query.
 *
 * @deprecated Pass breakpoint values as transient props to styled components
 * instead (e.g. `$isMobile`, `$isTablet`). Styled-component definitions are
 * evaluated at module load time, before any React component renders or any
 * container ref is attached, so it is not reliably possible to detect whether
 * a container or media query should be used, i.e. whether
 * `ContainerQueryProvider` is mounted at the point where the component renders.
 */
export const mediaQuery = (device, styles) => {
  return css(_t || (_t = _`
    @media screen and ${0} {
      ${0}
    }
  `), devices[device], styles);
};
const deriveFromContainerWidth = width => ({
  isMobile: width <= BREAKPOINTS.sm,
  isTablet: width <= BREAKPOINTS.md,
  isLaptop: width <= BREAKPOINTS.lg,
  isDesktop: width >= BREAKPOINTS.xl,
  isNotMobile: width >= BREAKPOINTS.sm,
  isNotTablet: width >= BREAKPOINTS.md,
  isNotLaptop: width >= BREAKPOINTS.lg,
  isMobileNavbar: width < NAVBAR_BREAKPOINT,
  isDesktopNavbar: width >= NAVBAR_BREAKPOINT
});
const windowMediaMatchers = {
  touchscreen: matchMedia(windowDevices.touchscreen),
  windowMobile: matchMedia(windowDevices.windowMobile),
  windowTablet: matchMedia(windowDevices.windowTablet),
  windowLaptop: matchMedia(windowDevices.windowLaptop),
  windowDesktop: matchMedia(windowDevices.windowDesktop),
  windowNotMobile: matchMedia(windowDevices.windowNotMobile),
  windowNotTablet: matchMedia(windowDevices.windowNotTablet),
  windowNotLaptop: matchMedia(windowDevices.windowNotLaptop)
};
const getWindowMatchState = () => ({
  isWindowMobile: windowMediaMatchers.windowMobile.matches,
  isWindowTablet: windowMediaMatchers.windowTablet.matches,
  isWindowLaptop: windowMediaMatchers.windowLaptop.matches,
  isWindowDesktop: windowMediaMatchers.windowDesktop.matches,
  isWindowNotMobile: windowMediaMatchers.windowNotMobile.matches,
  isWindowNotTablet: windowMediaMatchers.windowNotTablet.matches,
  isWindowNotLaptop: windowMediaMatchers.windowNotLaptop.matches
});
const useMediaQueryInternal = create(set => Object.assign({}, deriveFromContainerWidth(window.innerWidth), getWindowMatchState(), {
  containerWidth: window.innerWidth,
  isTouchscreen: windowMediaMatchers.touchscreen.matches,
  setContainerWidth: debounce(width => set(Object.assign({}, deriveFromContainerWidth(width), {
    containerWidth: width
  })), 50),
  setWindowMatches: debounce(() => set(Object.assign({}, getWindowMatchState())), 50)
}));
export const useMediaQuery = matchDevice => {
  const result = useMediaQueryInternal();
  return produce(result, draft => {
    if (matchDevice) {
      if (matchDevice in containerDevices) {
        const query = containerDevices[matchDevice];
        const maxMatch = query.match(/max-width:\s*(\d+)px/);
        const minMatch = query.match(/min-width:\s*(\d+)px/);
        if (maxMatch) {
          draft.matches = draft.containerWidth <= parseInt(maxMatch[1], 10);
        } else if (minMatch) {
          draft.matches = draft.containerWidth >= parseInt(minMatch[1], 10);
        }
      } else {
        var _windowMediaMatchers$, _windowMediaMatchers;
        draft.matches = (_windowMediaMatchers$ = (_windowMediaMatchers = windowMediaMatchers[matchDevice]) === null || _windowMediaMatchers === void 0 ? void 0 : _windowMediaMatchers.matches) !== null && _windowMediaMatchers$ !== void 0 ? _windowMediaMatchers$ : false;
      }
    }
  });
};
export const useCustomMediaQuery = customMediaQuery => {
  const [isMatching, setIsMatching] = useState(!!window.matchMedia(customMediaQuery).matches);
  useEffect(() => {
    const mql = window.matchMedia(customMediaQuery);
    const changeHandler = e => setIsMatching(!!e.matches);
    mql.addEventListener('change', changeHandler);
    return () => {
      mql.removeEventListener('change', changeHandler);
    };
  }, [customMediaQuery]);
  return isMatching;
};

// Container Query Provider
const ContainerQueryContext = /*#__PURE__*/createContext(null);
const GlobalContainerQueryVars = createGlobalStyle(_t2 || (_t2 = _`
  :root {
    --academy-bp-sm: ${0}px;
    --academy-bp-md: ${0}px;
    --academy-bp-lg: ${0}px;
    --academy-bp-xl: ${0}px;
  }
`), BREAKPOINTS.sm, BREAKPOINTS.md, BREAKPOINTS.lg, BREAKPOINTS.xl);
const ContainerQueryRoot = styled.div(_t3 || (_t3 = _`
  container-type: inline-size;
  container-name: ${0};
  contain: layout;
`), CONTAINER_NAME);
let containerMounted = false;
export const ContainerQueryProvider = ({
  children
}) => {
  const ref = useRef(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        useMediaQueryInternal.getState().setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(element);
    useMediaQueryInternal.getState().setContainerWidth(element.getBoundingClientRect().width);
    return () => {
      observer.disconnect();
      useMediaQueryInternal.getState().setContainerWidth(window.innerWidth);
    };
  }, []);
  const setContainerQueryRootRef = useCallback(node => {
    containerMounted = Boolean(node);
    ref.current = node;
  }, []);
  return /*#__PURE__*/_jsxs(ContainerQueryContext.Provider, {
    value: ref,
    children: [/*#__PURE__*/_jsx(GlobalContainerQueryVars, {}), /*#__PURE__*/_jsx(ContainerQueryRoot, {
      id: CONTAINER_NAME,
      ref: setContainerQueryRootRef,
      children: children
    })]
  });
};

// Window resize listener for window-based queries (navbar, window* variants)
const initWindowWatcher = (win = window) => {
  win.addEventListener('resize', () => {
    if (!containerMounted) {
      useMediaQueryInternal.getState().setContainerWidth(window.innerWidth);
    }
    useMediaQueryInternal.getState().setWindowMatches();
  });
};
initWindowWatcher();
export const academyPageFullWidth = css(_t4 || (_t4 = _`
  width: 100%;
  max-width: 1376px;
  margin: 0 auto;
`));
export const ACADEMY_PAGE_MAX_WIDTH = 1376;
export const academyResultsBar = css(_t5 || (_t5 = _`
  width: 100%;
  height: 40px;
  background-color: ${0};
`), ({
  theme: trellisTheme
}) => trellisTheme.color['fill-surface-raised']);
export const getSectionBackgroundStyle = backgroundColor => css(_t6 || (_t6 = _`
  position: relative;

  &::before {
    z-index: -1;
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: calc((100vw - 100%) / 2 * -1);
    height: 100%;
    width: 100vw;
    ${0}
  }
`), backgroundColor && `background-color: ${backgroundColor};`);